import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Heart } from "lucide-react";

const LINKS = [
  { to: "/", label: "Accueil" },
  { to: "/artistes", label: "Artistes" },
  { to: "/actualites", label: "Actualités" },
  { to: "/editions", label: "Créations" },
  { to: "/jukebox", label: "Jukebox" },
  { to: "/a-propos", label: "À propos" },
  { to: "/contact", label: "Contact" },
  { to: "/boutique", label: "Boutique" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const go = (to) => {
    setOpen(false);
    navigate(to);
  };

  return (
    <>
      <header data-testid="main-navbar" className="fixed top-0 z-50 w-full bg-jm-ink/85 backdrop-blur-xl border-b border-jm-brass/15">
        <div className="jm-container flex items-center justify-between py-4">
          <Link to="/" data-testid="navbar-logo" className="group block leading-none" aria-label="Joy Messenger — accueil">
            <span className="relative block h-12 sm:h-14" style={{ aspectRatio: "372 / 164" }}>
              <img src="/logo-base.png" alt="Joy Messenger" className="absolute inset-0 h-full w-full object-contain" />
              <img
                src="/logo.png"
                alt=""
                aria-hidden="true"
                className="jm-reel-spin absolute"
                style={{ left: "31.18%", top: "10.37%", width: "28.49%" }}
              />
            </span>
          </Link>
          <nav className="hidden lg:flex items-center gap-3 xl:gap-6" data-testid="navbar-links">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                className={({ isActive }) =>
                  `text-[13px] tracking-wide transition-colors duration-300 ${
                    isActive ? "text-jm-brass" : "text-jm-smoke hover:text-jm-bone"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <Link
              to="/soutenir"
              data-testid="nav-support-button"
              className="hidden sm:inline-flex items-center gap-2 border border-jm-amber/50 text-jm-amber hover:bg-jm-amber hover:text-jm-ink transition-colors duration-300 text-[13px] tracking-wide px-4 py-2"
            >
              <Heart size={13} strokeWidth={1.8} />
              Soutenir
            </Link>
            <button
              data-testid="nav-menu-toggle"
              onClick={() => setOpen(true)}
              className="lg:hidden text-jm-bone p-2"
              aria-label="Ouvrir le menu"
            >
              <Menu size={22} strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            className="fixed inset-0 z-[60] bg-jm-ink flex flex-col"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
          >
            <div className="jm-container flex items-center justify-between py-4 border-b border-jm-brass/15">
              <span className="relative block h-10" style={{ aspectRatio: "372 / 164" }}>
                <img src="/logo-base.png" alt="Joy Messenger" className="absolute inset-0 h-full w-full object-contain" />
                <img
                  src="/logo.png"
                  alt=""
                  aria-hidden="true"
                  className="jm-reel-spin absolute"
                  style={{ left: "31.18%", top: "10.37%", width: "28.49%" }}
                />
              </span>
              <button data-testid="nav-menu-close" onClick={() => setOpen(false)} className="p-2 text-jm-bone" aria-label="Fermer le menu">
                <X size={24} strokeWidth={1.5} />
              </button>
            </div>
            <nav className="jm-container flex-1 flex flex-col justify-center gap-1 overflow-y-auto">
              {[...LINKS, { to: "/soutenir", label: "Soutenir" }].map((l, i) => (
                <motion.button
                  key={l.to}
                  data-testid={`mobile-nav-${l.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                  onClick={() => go(l.to)}
                  className="text-left font-serif text-4xl sm:text-5xl py-2 text-jm-bone/90 hover:text-jm-brass transition-colors duration-300"
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i + 0.1, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                >
                  {l.label}
                </motion.button>
              ))}
            </nav>
            <div className="jm-container py-6 border-t border-jm-brass/15">
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-jm-mute">
                Plusieurs projets, une même aventure artistique
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
